import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import BoardColumn from './components/BoardColumn'
import styles from './index.module.scss'
import {Task} from "../../../../types/task";
import TaskService from '../../../../services/task.service';
import useTaskDragAndDrop, { mergeOrderedTaskSubset } from '../../hooks/useTaskDragAndDrop';
import { updateTaskChangedFields } from '../../utils/taskPayload';
import { getProjectTaskStatuses } from '../../utils/taskStatuses';
import { getWheelDeltaPx, scrollHorizontallyByWheel } from '../../../../utils/horizontalWheel';
import { filterArchivedTasks } from '../../../../utils/taskList';


interface BoardViewProps {
    getTasks: () => void
    onCreateTask?: (task: any) => Promise<any>
    canPersistOrder?: boolean
    onTaskUpdated?: (task: Task | null, options?: { remove?: boolean }) => void
    onTasksReordered?: (tasks: Task[]) => void
    tasks: Task[]
    allTasks?: Task[]
    project: any
    projectUsers?: any[]
}

export default function BoardView({ canPersistOrder = true, onCreateTask, onTaskUpdated, onTasksReordered, project, projectUsers = [], getTasks, tasks, allTasks = tasks}: BoardViewProps){
    const boardStatuses = useMemo(() => getProjectTaskStatuses(project), [project])
    const boardTasks = tasks
    const parentTitles = useMemo(() => new Map(allTasks.map((task) => [String(task.id), task.title])), [allTasks])
    const persistTaskStatus = useCallback(async (task: Task, status: string) => {
        const updatedTask = await updateTaskChangedFields(task.id, project.id, {
            status,
        })

        onTaskUpdated?.(updatedTask)

        return updatedTask
    }, [onTaskUpdated, project.id])
    const persistTaskOrder = useCallback(async ({ sourceTask, droppedTask, nextTasks }: {
        sourceTask: Task | null
        droppedTask: Task | null
        nextTasks: Task[]
    }) => {
        if (!canPersistOrder) {
            return
        }

        const statusesToPersist = Array.from(new Set([
            sourceTask?.status,
            droppedTask?.status,
        ].filter((status): status is string => Boolean(status))))

        // With a filter (e.g. "my tasks") the board shows only part of the
        // tasks — merge the visible order into the full list so the column
        // order sent to the backend keeps the hidden tasks too.
        const projectTasks = mergeOrderedTaskSubset(filterArchivedTasks(allTasks), nextTasks)

        await Promise.all(statusesToPersist.map(async (status) => {
            const taskIds = projectTasks
                .filter((task) => task.status === status)
                .map((task) => task.id)

            await TaskService.reorder({
                project_id: project.id,
                status,
                task_ids: taskIds,
            })
        }))
    }, [allTasks, canPersistOrder, project.id])
    const {
        beginDrag,
        commitDrop,
        draggedTaskId,
        dropTarget,
        endDrag,
        isDragging,
        orderedTasks,
        pendingTaskId,
        previewMove,
    } = useTaskDragAndDrop({
        tasks: boardTasks,
        onTasksCommitted: onTasksReordered,
        onOrderPersist: persistTaskOrder,
        onTaskStatusPersist: persistTaskStatus,
    })
    const formatedTasks = useMemo(() => {
        const formatedObject = boardStatuses.reduce((accum: any, status: any) => {
            return {
                ...accum,
                [status.key]: [],
            }
        }, {})

        orderedTasks.forEach((task) => {
            if (formatedObject[task.status]) {
                formatedObject[task.status] = [...formatedObject[task.status], task]
            }
        })

        return formatedObject
    }, [boardStatuses, orderedTasks])

    // The board scrolls horizontally inside its parent. A vertical mouse wheel
    // has nothing to scroll outside the column task lists, so translate it into
    // horizontal scroll — unless the pointer is over a column that can still
    // scroll vertically in that direction.
    const boardRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const board = boardRef.current
        const scroller = board?.parentElement
        if (!board || !scroller) return

        const handleWheel = (event: WheelEvent) => {
            // horizontal gestures (trackpad, shift+wheel) scroll natively
            if (event.shiftKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return

            const delta = getWheelDeltaPx(event.deltaY, event, scroller)

            const column = (event.target as HTMLElement | null)?.closest?.('[data-board-column-tasks]') as HTMLElement | null
            if (column) {
                const canScrollDown = column.scrollTop < column.scrollHeight - column.clientHeight - 1
                const canScrollUp = column.scrollTop > 0
                if ((delta > 0 && canScrollDown) || (delta < 0 && canScrollUp)) return
            }

            scrollHorizontallyByWheel(scroller, delta, event)
        }

        board.addEventListener('wheel', handleWheel, { passive: false })
        return () => board.removeEventListener('wheel', handleWheel)
    }, [boardStatuses.length])

    if (!boardStatuses.length) {
        return null
    }

    return (
        <div className={styles.boardView} ref={boardRef}>
            {boardStatuses.map((boardColumn) => (
                <BoardColumn
                    project={project}
                    projectUsers={projectUsers}
                    getTasks={getTasks}
                    key={boardColumn.key}
                    boardColumn={boardColumn}
                    draggedTaskId={draggedTaskId}
                    dropTarget={dropTarget}
                    isDragging={isDragging}
                    onCreateTask={onCreateTask}
                    onTaskDragEnd={endDrag}
                    onTaskDragStart={beginDrag}
                    onTaskDrop={commitDrop}
                    onTaskHover={previewMove}
                    onTaskUpdated={onTaskUpdated}
                    pendingTaskId={pendingTaskId}
                    canPersistOrder={canPersistOrder}
                    tasks={formatedTasks[boardColumn.key] || []}
                    parentTitles={parentTitles}
                />
            ))}
        </div>
    )
}
